/* =======================================================
   WC QUICK CART — Close (Empty CTA) + Multi-buy Promo (V2.1)
   - No inline scripts in sections (AJAX render safe)
   - Does NOT hijack Remove / quantity logic
   - Promo is COUNT-based (>=3 total items), cart.js truth + DOM fallback
   ======================================================= */
(() => {
  const QC_ROOT_SEL = ".quick-cart";
  const WRAP_SEL = ".quick-cart__wrapper";
  const ITEMS_SEL = ".quick-cart__items";

  const CONTINUE_SEL = ".quick-cart__empty-state-cta a[data-wc-qc-continue-close]";

  const PROMO_TEXT_SEL = "[data-wc-multibuy-promo-text]";
  const PROMO_ACTIVE_CLASS = "quick-cart__promo-text--active";

  const THRESHOLD = 3;

  const MSG_LOCKED = '<strong> Add 3+ items for 10% OFF</strong>';
  const MSG_UNLOCKED = '<strong>10% OFF unlocked ✓ </strong>';;

  const getRootPath = () =>
    (window.Shopify && Shopify.routes && Shopify.routes.root) ||
    (window.theme && theme.routes && theme.routes.root) ||
    "/";

  const cartJsUrl = () => `${getRootPath()}cart.js?ts=${Date.now()}`;

  async function fetchCart() {
    const res = await fetch(cartJsUrl(), {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });

    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (_) {
      throw new Error(`cart.js not JSON (status ${res.status})`);
    }
  }

  // ----- DOM fallback (ha cart.js épp fail / késik) -----
  function getDomCount(root) {
    if (!root) return 0;
    let sum = 0;
    root.querySelectorAll('input[name="updates[]"][data-quantity-input]').forEach((el) => {
      const v = parseInt(el.value, 10);
      if (!Number.isNaN(v)) sum += v;
    });
    if (sum > 0) return sum;

    // fallback: parse heading "Your cart (3)"
    const h = root.querySelector(".quick-cart__heading");
    const t = h ? (h.textContent || "") : "";
    const m = t.match(/\((\d+)\)/);
    return m ? parseInt(m[1], 10) : 0;
  }

  function applyPromoStateByCount(count) {
    const active = Number(count || 0) >= THRESHOLD;

    document.querySelectorAll(`${QC_ROOT_SEL} ${PROMO_TEXT_SEL}`).forEach((el) => {
      el.classList.toggle(PROMO_ACTIVE_CLASS, active);
      el.innerHTML = active ? MSG_UNLOCKED : MSG_LOCKED;
    });
  }

  // ---- Promo scheduler (throttled) ----
  let inFlight = null;
  let scheduled = false;
  let lastCount = null;

  async function updatePromo() {
    try {
      inFlight = inFlight || fetchCart();
      const cart = await inFlight;

      const count = Number(cart.item_count ?? 0);
      if (count !== lastCount) {
        lastCount = count;
        applyPromoStateByCount(count);
      } else {
        // still re-apply in case DOM got re-rendered and lost state
        applyPromoStateByCount(count);
      }
    } catch (_) {
      // Fail gracefully using DOM fallback (per root)
      document.querySelectorAll(QC_ROOT_SEL).forEach((root) => {
        applyPromoStateByCount(getDomCount(root));
      });
    } finally {
      inFlight = null;
    }
  }

  function schedulePromoUpdate() {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scheduled = false;
        updatePromo();
      });
    });
  }

  // ---- Close helper for Empty CTA ----
  function closeQuickCart(fromEl) {
    const root = fromEl.closest(QC_ROOT_SEL);
    if (!root) return;

    const wrap = root.querySelector(WRAP_SEL);
    const closeBtn = root.querySelector(".quick-cart__close-icon");
    const overlay = root.querySelector(".quick-cart__overlay");

    const clickTarget = closeBtn || overlay;
    if (clickTarget) {
      try {
        clickTarget.dispatchEvent(
          new MouseEvent("click", { bubbles: true, cancelable: true })
        );
      } catch (_) {}
    }

    // Ask your V15 locker to resync (it will only unlock if it locked)
    const resync = () => window.__WC_QC_V15__?.sync?.();

    requestAnimationFrame(() => requestAnimationFrame(resync));
    setTimeout(resync, 80);
    setTimeout(resync, 240);

    // Last-resort: if theme didn’t flip state for some reason
    setTimeout(() => {
      if (!wrap) return;
      const active = wrap.classList.contains("active");
      const hidden = wrap.getAttribute("aria-hidden") === "true";
      if (active && !hidden) {
        wrap.classList.remove("active");
        wrap.setAttribute("aria-hidden", "true");
      }
      resync();
    }, 380);
  }

  // Empty-state CTA close (only this target!)
  document.addEventListener(
    "click",
    (e) => {
      const a = e.target.closest(CONTINUE_SEL);
      if (!a) return;

      e.preventDefault();
      // IMPORTANT: no stopPropagation (don’t break theme click logic)
      closeQuickCart(a);
    },
    true
  );

  // Promo update triggers — THEME REAL SELECTORS
  document.addEventListener(
    "click",
    (e) => {
      const root = e.target.closest(QC_ROOT_SEL);
      if (!root) return;

      const hit =
        e.target.closest("[data-add-quantity]") ||
        e.target.closest("[data-subtract-quantity]") ||
        e.target.closest("button[data-remove-item]") ||
        e.target.closest('form[action*="/cart/add"] button[type="submit"]') ||
        e.target.closest('button[name="add"]') ||
        e.target.closest('button[type="submit"][name="add"]');

      if (!hit) return;

      // nudge a few times because theme updates async
      setTimeout(schedulePromoUpdate, 60);
      setTimeout(schedulePromoUpdate, 180);
      setTimeout(schedulePromoUpdate, 420);
    },
    true
  );

  // Observe QC content changes (re-render / qty change / remove)
  function attachObserversToRoot(root) {
    if (!root || root.__wcPromoObsBound) return;
    root.__wcPromoObsBound = true;

    const items = root.querySelector(ITEMS_SEL) || root;
    const mo = new MutationObserver(() => {
      // DOM changed => update promo
      schedulePromoUpdate();
    });

    mo.observe(items, { childList: true, subtree: true, characterData: true, attributes: true });
  }

  // If QC is injected/re-rendered, attach observers + update promo
  const bootMo = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const n of m.addedNodes || []) {
        if (n.nodeType !== 1) continue;

        if (n.matches?.(QC_ROOT_SEL)) {
          attachObserversToRoot(n);
          schedulePromoUpdate();
          continue;
        }
        const found = n.querySelector?.(QC_ROOT_SEL);
        if (found) {
          attachObserversToRoot(found);
          schedulePromoUpdate();
        }
      }
    }
  });
  bootMo.observe(document.documentElement, { childList: true, subtree: true });

  // Theme events (harmless if never fired)
  document.addEventListener("cart:updated", schedulePromoUpdate);
  document.addEventListener("cart:change", schedulePromoUpdate);
  document.addEventListener("quick-cart:open", schedulePromoUpdate);
  document.addEventListener("quick-cart:opened", schedulePromoUpdate);
  window.addEventListener("pageshow", schedulePromoUpdate, { passive: true });

  // Init
  document.querySelectorAll(QC_ROOT_SEL).forEach(attachObserversToRoot);
  schedulePromoUpdate();
  })();


/* =======================================================
   WC QC — Footer Scroll Bridge (V2 · delegated · rerender-proof)
   Fix: if user scrolls on footer/promo, forward scroll to the
   drawer’s actual scroll container, even after AJAX re-renders.
   Safe: does not touch remove/qty/add logic.
   ======================================================= */
(() => {
  if (window.__WC_QC_FOOTER_SCROLL_BRIDGE_V2__) return;
  window.__WC_QC_FOOTER_SCROLL_BRIDGE_V2__ = true;

  const ROOT_SEL = ".quick-cart";
  const WRAP_SEL = ".quick-cart__wrapper";
  const TARGETS_SEL = ".quick-cart__footer, .quick-cart__promo-message";

  const SCROLLER_CANDIDATES = [
    ".quick-cart__main-content",
    ".quick-cart__items",
    ".quick-cart__container",
  ];

  const isOpen = (root) => {
    const wrap = root.querySelector(WRAP_SEL);
    if (!wrap) return false;
    const active = wrap.classList.contains("active");
    const hidden = wrap.getAttribute("aria-hidden") === "true";
    return active && !hidden;
  };

  const isScrollable = (el) => {
    if (!el) return false;
    return el.scrollHeight > el.clientHeight + 2;
  };

  const getScroller = (root) => {
    // Prefer actually scrollable containers first
    for (const sel of SCROLLER_CANDIDATES) {
      const el = root.querySelector(sel);
      if (isScrollable(el)) return el;
    }
    // Fallback: first candidate that exists
    for (const sel of SCROLLER_CANDIDATES) {
      const el = root.querySelector(sel);
      if (el) return el;
    }
    return null;
  };

  // ---- WHEEL (trackpad/mouse) ----
  document.addEventListener(
    "wheel",
    (e) => {
      const target = e.target.closest(TARGETS_SEL);
      if (!target) return;

      const root = target.closest(ROOT_SEL);
      if (!root || !isOpen(root)) return;

      const scroller = getScroller(root);
      if (!scroller) return;

      // Only react to vertical intent
      const dy = e.deltaY || 0;
      if (!dy) return;

      e.preventDefault();
      scroller.scrollTop += dy;
    },
    { capture: true, passive: false }
  );

  // ---- TOUCH (mobile) ----
  let touch = null;

  document.addEventListener(
    "touchstart",
    (e) => {
      const target = e.target.closest(TARGETS_SEL);
      if (!target) return;

      const root = target.closest(ROOT_SEL);
      if (!root || !isOpen(root)) return;

      const scroller = getScroller(root);
      if (!scroller) return;

      touch = { scroller, y: e.touches[0].clientY };
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    "touchmove",
    (e) => {
      if (!touch) return;
      const y = e.touches[0].clientY;
      const dy = touch.y - y;
      touch.y = y;

      e.preventDefault();
      touch.scroller.scrollTop += dy;
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    "touchend",
    () => (touch = null),
    { capture: true, passive: true }
  );
  document.addEventListener(
    "touchcancel",
    () => (touch = null),
    { capture: true, passive: true }
  );
})();
